import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { isFirebaseConfigured } from '../config/firebase';
import { onAuthChange, logout as authLogout, getIdToken } from '../services/auth';
import {
  getUserProfile,
  createUserProfile,
  updateLastLogin,
  getOrganization,
  UserProfile,
  Organization,
  UserRole,
} from '../services/firestore';

interface AuthContextType {
  // Firebase user
  user: User | null;
  // Firestore user profile
  userProfile: UserProfile | null;
  // User's organization
  organization: Organization | null;
  // Loading states
  loading: boolean;
  profileLoading: boolean;
  // Auth state
  isAuthenticated: boolean;
  isPending: boolean;
  isActive: boolean;
  isSuspended: boolean;
  // Role checks (actual role from Firestore)
  role: UserRole | null;
  isPlatformAdmin: boolean;
  isCRAAdmin: boolean;
  isCRAAnalyst: boolean;
  isRatingCommittee: boolean;
  isBankAdmin: boolean;
  isBankUser: boolean;
  // Actions
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  getToken: () => Promise<string | null>;
  // Note: For effective role (respecting role assumption), use useRoleAssumption() hook
  // The role properties above are the ACTUAL role from Firestore
  // Use effectiveRole from RoleAssumptionContext for UI/routing that should respect assumed roles
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Fetch or create user profile
  const fetchUserProfile = useCallback(async (firebaseUser: User) => {
    setProfileLoading(true);
    try {
      let profile = await getUserProfile(firebaseUser.uid);

      if (!profile) {
        // Create new user profile for first-time users
        const authProvider = firebaseUser.providerData[0]?.providerId === 'google.com'
          ? 'google'
          : 'password';

        profile = await createUserProfile(
          firebaseUser.uid,
          firebaseUser.email || '',
          firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          authProvider,
          firebaseUser.photoURL || undefined
        );
      } else {
        // Update last login for existing users
        await updateLastLogin(firebaseUser.uid);
      }

      setUserProfile(profile);

      // Fetch organization if user has one
      if (profile.org_id) {
        const org = await getOrganization(profile.org_id);
        setOrganization(org);
      } else {
        setOrganization(null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
      setOrganization(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    // If Firebase is not configured, skip auth and allow access
    if (!isFirebaseConfigured) {
      console.warn('Firebase not configured - running in development mode without authentication');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthChange(async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        await fetchUserProfile(firebaseUser);
      } else {
        setUserProfile(null);
        setOrganization(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  // Logout handler
  const logout = useCallback(async () => {
    try {
      await authLogout();
      setUser(null);
      setUserProfile(null);
      setOrganization(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  }, []);

  // Refresh user profile
  const refreshUserProfile = useCallback(async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  }, [user, fetchUserProfile]);

  // Get ID token for API calls
  const getToken = useCallback(async (): Promise<string | null> => {
    return getIdToken();
  }, []);

  // Computed values
  const isAuthenticated = !!user;
  const isPending = userProfile?.status === 'pending';
  const isActive = userProfile?.status === 'active';
  const isSuspended = userProfile?.status === 'suspended';
  const role = userProfile?.role || null;

  // Role checks
  const isPlatformAdmin = role === 'platform_admin';
  const isCRAAdmin = role === 'cra_admin';
  const isCRAAnalyst = role === 'cra_analyst';
  const isRatingCommittee = role === 'rating_committee';
  const isBankAdmin = role === 'bank_admin';
  const isBankUser = role === 'bank_user';

  const value: AuthContextType = {
    user,
    userProfile,
    organization,
    loading,
    profileLoading,
    isAuthenticated,
    isPending,
    isActive,
    isSuspended,
    role,
    isPlatformAdmin,
    isCRAAdmin,
    isCRAAnalyst,
    isRatingCommittee,
    isBankAdmin,
    isBankUser,
    logout,
    refreshUserProfile,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
